// Pure helpers for turning the admin's image_type rows into the
// customer-facing checklist rendered by PackageDisplayV2.
//
// The admin writes one row per printing sheet (e.g. "(1) Magnets" =
// one sheet). The customer sees the count of individual items after
// grouping duplicates and applying per-label multipliers. Kept as
// pure functions with no React dependency so they're straightforward
// to unit-test.

// Which checklist rows are digital. Mirrors the server's ONE rule for the
// same question -- ImageType.downloadable (app/models/image_type.rb:8),
// which matches a name containing "digital" OR "download". No boolean is
// exposed on the API resource, so name-matching is the contract on both
// sides; keep the two in step.
export const isDownloadItem = (label) => /download|digital/i.test(label)

// Does this option include anything that physically ships?
//
// The server charges shipping unless every order package is
// downloadable-only, and it decides THAT from the built sheets
// (OrderPackage#downloadable_only? -> Cart#shipping) -- the option's
// digital_images_only flag is never consulted. At pricelist time no sheets
// exist yet, so the collapsed image_type rows are the closest available
// proxy: any non-download row is something that physically ships.
//
// `fallback` is returned when there are no rows to inspect (an option with
// no image_types configured), so the caller decides what to assume rather
// than this silently answering "never ships".
export const hasPhysicalItem = (items, fallback = true) => (
  items.length > 0 ? items.some((label) => !isDownloadItem(label)) : fallback
)

// Parse an image_type name of the form "(N) Label" into { count, label }.
// Rows that don't match (e.g. free-text notes) become { count: 1, label }.
//
// "(1) Magnets" special case: the admin writes one line-item per printing
// sheet, but each printed sheet yields TWO individual magnets. Rewrite
// the exact count-1 case to (2) so the card shows the true customer-
// facing count, while the DB / admin UI keep their "one sheet = one
// line" convention. An admin-written "(N) Magnets" for N >= 2 is left
// alone -- we only rewrite the count that has to be a shorthand.
//
// The label is matched with an optional "Photo" qualifier, because admins
// write the same sheet both ways ("(1) Magnet", "(1) Magnets", "(1) Photo
// Magnet" -- the elementary Star Student option uses the last). All of
// them collapse to the canonical "Magnet" label so the count rewrite and
// the duplicate-grouping key are the same whichever wording was typed,
// and every card reads "(2) Magnets".
//
// SIZED magnet rows are deliberately excluded: "(1) 5x7 Magnet" is one
// 5x7 magnet, not a two-up sheet, and admins already write sized sheets
// with their real count ("(2) 4x5 Magnets"). Only the unsized wording is
// the one-sheet shorthand.
const MAGNET_SHEET = /^(photo\s+)?magnets?$/i

export const parseItem = (name) => {
  const m = name.match(/^\((\d+)\)\s*(.+)$/)
  if (m) {
    let count = parseInt(m[1], 10)
    let label = m[2].trim()
    if (MAGNET_SHEET.test(label)) {
      label = 'Magnet'
      if (count === 1) count = 2
    }
    return { count, label }
  }
  return { count: 1, label: name.trim() }
}

// Normalize a label for grouping: case-insensitive, whitespace-collapsed,
// with a trailing 's' stripped so singular + plural rows ("Download",
// "Downloads") merge. This is the *comparison key* only; the displayed
// label keeps its original casing/plural form.
const normalizeLabel = (label) => (
  label.toLowerCase().replace(/\s+/g, ' ').replace(/s$/, '')
)

// Same label + apostrophe/curly-quote-normalized in case admin rows use
// smart quotes ("8x10's" vs "8x10s").
const canonicalKey = (label) => (
  normalizeLabel(label).replace(/[’']/g, '')
)

// Sum repeats: "(1) High-Resolution Download" x3 -> "(3) High-Resolution
// Downloads". Display uses whichever variant already had the most
// characters (usually the plural), then re-pluralizes if the total is >1
// and the label doesn't already end in 's'.
export const collapseItems = (rawItems) => {
  const groups = new Map()
  rawItems.forEach((raw) => {
    const { count, label } = parseItem(raw)
    const key = canonicalKey(label)
    if (groups.has(key)) {
      const g = groups.get(key)
      g.count += count
      // Prefer the longer variant (usually the plural) for display.
      if (label.length > g.label.length) g.label = label
    } else {
      groups.set(key, { count, label })
    }
  })
  return Array.from(groups.values()).map(({ count, label }) => {
    // Only pluralize when it's obviously a word: skip if already plural
    // (ends in s) or if it's a size string ending in a digit (8x10 / 5x7
    // stays the same regardless of count).
    const needsPlural = (
      count > 1
      && !/s$/i.test(label)
      && !/\d$/.test(label)
    )
    const displayLabel = needsPlural ? `${label}s` : label
    return `(${count}) ${displayLabel}`
  })
}
